// Νέα προκαταβολή στο ταμείο, για κράτηση ή για πελάτη (γενική πίστωση, με τηλέφωνο).
// Κάρτα/IRIS: ο server υπογράφει το 8.4, χρεώνει στο τερματικό και εκδίδει το 8.4 — το αίτημα
// περιμένει όσο το τερματικό (≤ ~3′). Μετρητά: καμία χρέωση, κανένα παραστατικό, στην καρτέλα.
// Κανόνες κλειδιού/επανάληψης: useChargeAttempt.

import React, { useMemo, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Btn, Field, Note, inputClass } from '../dineIn/floorEditor/ui';
import { depositsApi, type ChargeOutcome } from './api';
import { MAX_CENTS, centsToApi, centsToInput, digits, dueCents, money, parseAmount, validPhone } from './model';
import { Choice, OutcomeNote, ReceiptLine, Shell, Uncertain, Waiting, useDT } from './ui';
import { useChargeAttempt } from './useChargeAttempt';
import { pickTerminal, savedTerminalId, useActiveTerminals } from './useDepositsConfig';
import type { CreateDepositBody, CreateDepositResponse, DepositMethod, DepositView, DepositsConfig } from './types';

export interface DepositReservation {
  id: number;
  customer_name: string;
  customer_phone?: string | null;
  reservation_date: string;
  reservation_time: string;
  party_size: number;
  deposit_due?: number | string | null;
  deposit?: { due?: number | null } | null;
}

export type DepositOwner =
  | { kind: 'reservation'; reservation: DepositReservation }
  | { kind: 'customer'; phone?: string; name?: string; customerId?: number };

/** Ό,τι θυμόμαστε από μια αβέβαιη προσπάθεια, για να ξαναδείξουμε τι στάλθηκε. */
type AttemptMeta = { cents: number; method: DepositMethod; terminalId: number | null; phone?: string; name?: string };

const ownerMemo = (owner: DepositOwner, phone: string) =>
  owner.kind === 'reservation' ? `deposit:r:${owner.reservation.id}`
    : owner.customerId ? `deposit:c:${owner.customerId}` : digits(phone).length >= 10 ? `deposit:p:${digits(phone)}` : null;

export default function DepositModal({ storeId, config, owner, onClose, onDone }: {
  storeId: number;
  config: DepositsConfig;
  owner: DepositOwner;
  onClose: () => void;
  /** Μετά από κάθε αποτέλεσμα που αλλάζει κάτι στον server (επιτυχία ή αβέβαιο): ανανέωση λιστών. */
  onDone?: (payment: DepositView | null) => void;
}) {
  const dt = useDT();
  const { employee } = useAuth();
  const res = owner.kind === 'reservation' ? owner.reservation : null;
  const resumeId = res ? `deposit:r:${res.id}` : owner.kind === 'customer' && owner.customerId ? `deposit:c:${owner.customerId}` : null;
  const attempt = useChargeAttempt<CreateDepositResponse, AttemptMeta>(resumeId);
  const prev = attempt.resumed;

  const [amount, setAmount] = useState(() => (prev ? centsToInput(prev.cents) : res && dueCents(res) > 0 ? centsToInput(dueCents(res)) : ''));
  // Χωρίς ηλεκτρονικό πάροχο μένουν μόνο τα μετρητά· αλλιώς ο ταμίας διαλέγει ρητά.
  const [method, setMethod] = useState<DepositMethod | null>(prev ? prev.method : config.ecr ? null : 'cash');
  const [terminalId, setTerminalId] = useState<number | null>(prev ? prev.terminalId : null);
  const [phone, setPhone] = useState(prev?.phone ?? (owner.kind === 'customer' ? owner.phone || '' : ''));
  const [name, setName] = useState(prev?.name ?? (owner.kind === 'customer' ? owner.name || '' : ''));
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  const terminalPay = method === 'card' || method === 'iris';
  const { terminals } = useActiveTerminals(storeId, config.ecr);
  // Το τερματικό: ό,τι διάλεξε ο ταμίας, αλλιώς το προτιμώμενο του ταμία / το μοναδικό.
  const autoTerminal = useMemo(() => pickTerminal(terminals, [savedTerminalId(employee?.id, storeId)]), [terminals, employee?.id, storeId]);
  const termId = terminalId ?? autoTerminal;

  const cents = parseAmount(amount);
  const errors = {
    amount: cents == null || cents <= 0 ? dt('deposits:err.amount') : cents > MAX_CENTS ? dt('deposits:err.amountMax', { amount: money(MAX_CENTS / 100) }) : null,
    method: !method ? dt('deposits:err.method') : null,
    terminal: terminalPay && !termId ? dt('deposits:err.terminal') : null,
    phone: owner.kind === 'customer' && !owner.customerId && !validPhone(phone) ? dt('deposits:err.phone') : null,
    name: owner.kind === 'customer' && !owner.customerId && !name.trim() ? dt('deposits:err.name') : null,
  };
  const valid = !Object.values(errors).some(Boolean);
  const show = (e: string | null) => (touched ? e : null);

  const { phase, outcome, locked, charging } = attempt;
  const done = phase === 'ok' && outcome?.kind === 'ok' ? outcome.data : null;
  const closable = !charging;

  const send = (clientKey: string): Promise<ChargeOutcome<CreateDepositResponse>> => {
    const body: CreateDepositBody = { method: method!, amount: centsToApi(cents!), clientKey };
    if (terminalPay && termId) body.terminalId = termId;
    if (res) body.reservationId = res.id;
    else if (owner.kind === 'customer' && owner.customerId) body.customerId = owner.customerId;
    else body.customer = { name: name.trim(), phone: phone.trim() };
    if (note.trim()) body.note = note.trim();
    return depositsApi.create(storeId, body).then(out => {
      if (out.kind !== 'ok') return out;
      // Replay προσπάθειας που ακόμη χρεώνεται / που απέτυχε στο μεταξύ.
      const st = out.data.payment?.status;
      if (st === 'pending') return { kind: 'uncertain', message: null, code: 'pending', id: out.data.payment.id };
      if (st === 'failed') return { kind: 'declined', message: out.data.message || null, data: out.data };
      return out;
    });
  };

  const submit = async () => {
    if (charging) return;
    // Αβέβαιη προσπάθεια: ξαναστέλνεται ΑΥΤΟΥΣΙΑ (ίδιο κλειδί) — τα πεδία είναι κλειδωμένα.
    if (phase !== 'uncertain' && !valid) { setTouched(true); return; }
    const memo = { id: ownerMemo(owner, phone), meta: { cents: cents!, method: method!, terminalId: terminalPay ? termId : null, phone, name } };
    const out = await attempt.run(send, memo);
    if (!out) return;
    if (out.kind === 'ok') onDone?.(out.data.payment || null);
    else if (out.kind === 'uncertain') onDone?.(null);
  };

  const title = res ? dt('deposits:modal.titleReservation') : dt('deposits:modal.titleCustomer');
  const sub = res ? `${res.customer_name} · ${res.reservation_date.split('-').reverse().slice(0, 2).join('/')} ${res.reservation_time.slice(0, 5)} · ${dt('deposits:people', { count: res.party_size })}` : undefined;
  const label = cents && cents > 0 ? money(cents / 100) : '';
  const methodName = (m: DepositMethod) => dt(`deposits:method.${m}`);

  if (done) {
    const p = done.payment;
    const cash = p?.method === 'cash';
    return (
      <Shell title={title} sub={sub} closable onClose={onClose} testId="deposit-modal"
        footer={<Btn variant="primary" onClick={onClose}>{dt('deposits:close')}</Btn>}>
        <div role="status" data-testid="deposit-ok" className="grid gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4">
          <p className="flex items-center gap-2 text-[17px] font-bold text-emerald-700 dark:text-emerald-300">
            <CheckCircle className="h-6 w-6 shrink-0" />{dt('deposits:modal.okTitle')}
          </p>
          <p className="text-[22px] font-bold tabular-nums text-t1">{money(p?.amount ?? (cents || 0) / 100)} · {methodName(p?.method || method || 'cash')}</p>
          <ReceiptLine doc="8.4" receipt={done.receipt || p?.receipt} pending={!cash && !(done.receipt?.mark || p?.receipt?.mark)}
            error={done.receipt_error || p?.receipt_error} cash={cash} />
          {cash && <p className="text-[13px] text-t2">{dt('deposits:modal.okCash', { amount: money(p?.amount ?? (cents || 0) / 100) })}</p>}
          {done.replay && <p className="text-[12.5px] text-t3">{dt('deposits:modal.okReplay')}</p>}
        </div>
      </Shell>
    );
  }

  const chargingTitle = method === 'card' ? dt('deposits:modal.chargingCard') : method === 'iris' ? dt('deposits:modal.chargingIris') : dt('deposits:modal.chargingCash');
  const uncertainCode = outcome?.kind === 'uncertain' ? outcome.code : null;

  const footer = phase === 'uncertain' ? (
    <>
      <Btn onClick={onClose}>{dt('deposits:close')}</Btn>
      <Btn variant="primary" onClick={submit} data-testid="deposit-check">{dt('deposits:modal.checkAgain')}</Btn>
    </>
  ) : (
    <>
      <Btn onClick={onClose} disabled={!closable}>{dt('deposits:cancel')}</Btn>
      <Btn variant="primary" className="h-12 px-5 text-[15px]" disabled={charging} onClick={submit} data-testid="deposit-submit">
        {terminalPay ? dt('deposits:modal.charge', { amount: label }) : method === 'cash' ? dt('deposits:modal.record', { amount: label }) : dt('deposits:modal.submit')}
      </Btn>
    </>
  );

  return (
    <Shell title={title} sub={sub} closable={closable} onClose={onClose} footer={footer} testId="deposit-modal">
      {charging && <Waiting title={chargingTitle} hint={terminalPay ? dt('deposits:modal.chargingHint') : undefined} />}
      {phase === 'uncertain' && (
        <Uncertain title={method === 'cash' ? dt('deposits:modal.uncertainCash') : dt('deposits:modal.uncertain')}>
          {uncertainCode === 'pending' && <p>{dt('deposits:modal.pending')}</p>}
          {uncertainCode === 'resumed' && prev && <p>{dt('deposits:modal.resumed', { amount: money(prev.cents / 100), method: methodName(prev.method) })}</p>}
          {(uncertainCode === 'network' || uncertainCode === 'timeout') && <p>{dt('deposits:modal.lost')}</p>}
          <p>{dt('deposits:modal.checkHint')}</p>
        </Uncertain>
      )}
      <OutcomeNote out={outcome} declinedTitle={dt('deposits:modal.declined')} />

      <fieldset disabled={locked} className="grid gap-4 disabled:opacity-60">
        {owner.kind === 'customer' && !owner.customerId && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={dt('deposits:modal.phone')} error={show(errors.phone)}>
              <input className={inputClass} type="tel" inputMode="tel" value={phone} autoFocus onChange={e => setPhone(e.target.value)} data-testid="deposit-phone" />
            </Field>
            <Field label={dt('deposits:modal.name')} error={show(errors.name)}>
              <input className={inputClass} value={name} onChange={e => setName(e.target.value)} data-testid="deposit-name" />
            </Field>
          </div>
        )}
        <Field label={dt('deposits:modal.amount')} error={show(errors.amount)}
          hint={res && dueCents(res) > 0 ? dt('deposits:modal.amountDue', { amount: money(dueCents(res) / 100) }) : undefined}>
          <input className={`${inputClass} h-12 text-[20px] font-bold tabular-nums`} inputMode="decimal" placeholder="0,00" value={amount}
            onChange={e => setAmount(e.target.value)} data-testid="deposit-amount" autoFocus={owner.kind === 'reservation'} />
        </Field>

        <div role="group" aria-label={dt('deposits:modal.method')} className="grid gap-1.5">
          <span className="text-[13px] font-medium text-t2">{dt('deposits:modal.method')}</span>
          <div className="grid grid-cols-3 gap-2">
            {(['card', 'iris', 'cash'] as DepositMethod[]).map(m => (
              <Choice key={m} on={method === m} disabled={m !== 'cash' && !config.ecr} onClick={() => setMethod(m)} testId={`deposit-method-${m}`}>
                {methodName(m)}
              </Choice>
            ))}
          </div>
          {!config.ecr && <span className="text-[12px] text-t3">{dt('deposits:modal.ecrRequired')}</span>}
          {show(errors.method) && <span className="text-[12.5px] text-red-600 dark:text-red-400">{errors.method}</span>}
        </div>

        {terminalPay && (
          <div role="group" aria-label={dt('deposits:modal.terminal')} className="grid gap-1.5">
            <span className="text-[13px] font-medium text-t2">{dt('deposits:modal.terminal')}</span>
            {terminals == null ? <div className="h-12 animate-pulse rounded-xl bg-surface-2" />
              : terminals.length === 0 ? <Note tone="warn">{dt('deposits:modal.noTerminals')}</Note> : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                  {terminals.map(tm => (
                    <Choice key={tm.id} on={termId === tm.id} onClick={() => setTerminalId(tm.id)} testId={`deposit-terminal-${tm.id}`}>
                      {tm.terminal_name || tm.terminal_id}
                    </Choice>
                  ))}
                </div>
              )}
            {show(errors.terminal) && terminals && terminals.length > 0 && <span className="text-[12.5px] text-red-600 dark:text-red-400">{errors.terminal}</span>}
          </div>
        )}

        {method && <p className="text-[12.5px] text-t3">{terminalPay ? dt('deposits:modal.hintCard') : dt('deposits:modal.hintCash')}</p>}

        <Field label={dt('deposits:modal.note')}>
          <textarea className={`${inputClass} h-16 py-2`} maxLength={500} value={note} onChange={e => setNote(e.target.value)} />
        </Field>
      </fieldset>
    </Shell>
  );
}
